import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SearchResult } from '@vta/types';
import { apiClient } from '../../lib/api-client.ts';

interface SearchResponse {
  results: SearchResult[];
}

/**
 * Debounced TanStack Query search hook.
 *
 * Behaviour:
 *   - Empty/whitespace query → query disabled, returns `results: []`.
 *   - Typing → 200ms debounce, then a single in-flight request. Older
 *     requests are cancelled by the AbortSignal piped through api-client.
 *   - `keepPreviousData` ensures the result list does not flicker while the
 *     user types past a partial match.
 */
export function useSearch(rawQuery: string, enabled = true) {
  const debounced = useDebounced(rawQuery.trim(), 200);

  return useQuery<SearchResponse>({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) =>
      apiClient.get<SearchResponse>('/api/search', { query: { q: debounced }, signal }),
    enabled: enabled && debounced.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

function useDebounced<T>(value: T, delay: number): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setHeld(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return held;
}
